import { realDealFeatures as Features } from '@wix/premium-marketing-data/dist/src/marketing-data/real-deal/features';
import { DateFormatter, FormatConfig } from '@wix/premium-react-text';
import { ExperimentsBag } from '@wix/wix-experiments';
import dayjs from 'dayjs';
import { StringMap } from 'i18next';
import { getSpecifiedCountryFaqs } from '../components/Faq/SpecificCountriesFaqUtils';
import { CycleMultiplier, faqCodeMap, faqEnterpriseCodeMap } from '../constants';
import {
  Country,
  Experiments,
  PackagePickerType,
  PaymentCycle,
  ProductFamily,
  RealEstateId,
  SectionCategory,
  Step,
  Template,
  Theme,
  VoucherEligibleTlds,
} from '../enums';
import { FeaturesBuilder } from '../server/Builders/FeaturesBuilder';
import {
  AdvancedPlansFeature,
  AdvancedPlansProductData,
  CycleDetails,
  Denullified,
  Feature,
  IAppContext,
  OfferingProduct,
  OfferingProductV2,
  ParsedAppIntentElements,
  ParsedDealerOffersMap,
  ParsedPaymentMethods,
} from '../types';
import { RevShareData } from '@wix/package-picker-common';
import { ProductInfo } from '@wix/ambassador-premium-store/types';

interface calculateRevShareSubtitleAmountProps {
  currentStep: Step;
  cycles: CycleDetails[];
  currentCycle: string;
  revShareData?: RevShareData;
  productGuid?: string;
}

interface calculateCycleRevShareProps {
  cycle: CycleDetails;
  revShareData: RevShareData;
  productGuid?: string;
}

export const getFeatureTooltipKey = (
  feature: Feature,
  appContext: IAppContext,
) => {
  const { country } = appContext;
  if (
    feature.code === Features.free_domain_1_year.code &&
    (country === Country.FR || country === Country.BR || country === Country.DE)
  ) {
    return `features.${feature.code}_${country.toLowerCase()}.tooltip`;
  }
  return `features.${feature.code}.tooltip`;
};

export const isCycleOfMonths = (cycle: string) =>
  cycle === PaymentCycle.MONTHLY || cycle === PaymentCycle.QUARTERLY;

export const deNullify = (n: number | null): number => n ?? 0;

export function deNullifyProduct(product: OfferingProduct | OfferingProductV2) {
  return Object.entries(product).reduce(
    (acc, [key, value]) => ({ ...acc, ...{ [key]: deNullify(value) } }),
    {} as Denullified<OfferingProduct | OfferingProductV2>,
  );
}

export const throttle = (func: Function, timeFrame: number) => {
  let lastTime = 0;
  return function (this: any) {
    const now = Date.now();
    if (now - lastTime >= timeFrame) {
      func.apply(this, arguments);
      lastTime = now;
    }
  };
};

const ppTypePageModeMap = {
  [PackagePickerType.Default]: {
    theme: Theme.AdvancedPlans,
    template: Template.Grid,
  },
  [PackagePickerType.EditorX]: {
    theme: Theme.EditorX,
    template: Template.Grid,
  },
  [PackagePickerType.Dayful]: {
    theme: Theme.Dayful,
    template: Template.Grid,
  },
  [PackagePickerType.Studio]: {
    theme: Theme.WixStudio,
    template: Template.Grid,
  },
  [PackagePickerType.EditorXToStudio]: {
    theme: Theme.WixStudio,
    template: Template.Grid,
  },
  [PackagePickerType.Sunrise]: {
    theme: Theme.AdvancedPlans,
    template: Template.Grid,
  },
};

export const getPageModeDataByPpType = (
  packagePickerType: PackagePickerType,
): { theme: Theme; template: Template } => {
  return ppTypePageModeMap[packagePickerType ?? PackagePickerType.Default];
};

export function replaceTrigonalBrackets(str: string, replaceValue: string) {
  const pattern = /(<\d*\/>|(<\d*>)|(<\/\d*>))/g;
  return str.replace(pattern, replaceValue);
}

export const translateAllKeyInObject = (obj, t) => {
  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      return { ...acc, ...{ [key]: t(value) } };
    },
    {
      title: '',
      content: '',
      ctaText: '',
    },
  );
};

export const getAdVoucherTransParams = (
  currencyToString,
  amount,
  amountToSpend,
) => {
  const formatConfig: FormatConfig = { hideZeroDecimals: true };
  const voucherTransParams = { amountToSpend: '$25', amount: '$75' };
  try {
    voucherTransParams.amountToSpend = currencyToString(
      amountToSpend!,
      formatConfig,
    );
    voucherTransParams.amount = currencyToString(amount!, formatConfig);
  } catch {
    // ignore
  }
  return voucherTransParams;
};

export const getPaymentMethodsFromDealerOffers = (
  offers: ParsedDealerOffersMap,
) => {
  const acceptedPaymentMethodsOffer = offers.get(
    RealEstateId.AcceptPaymentMethods,
  ) as ParsedPaymentMethods;
  return acceptedPaymentMethodsOffer;
};

export function instanceOfOfferingProductV2(
  object: Denullified<OfferingProduct | OfferingProductV2>,
): object is Denullified<OfferingProductV2> {
  return (object as OfferingProductV2).cyclePrices !== undefined;
}

const FALLBACK_DISCOUNT_PERCENTAGE: number = 50;

export function getSaleDiscountPercentage(): number {
  const productDiscountPercentage = FALLBACK_DISCOUNT_PERCENTAGE;
  return productDiscountPercentage;
}

export const getFaqs = ({
  hasGoogleVoucher,
  theme,
  isEnterprisePlanType = false,
  localizedFaqs,
  country,
  isEligibleForRevShare
}: {
  hasGoogleVoucher: boolean;
  theme: Theme;
  isEnterprisePlanType?: boolean;
  localizedFaqs?: boolean;
  country: Country;
  isEligibleForRevShare?: boolean;
}): string[] => {
  let themedFaqs = faqCodeMap[theme];

  if (theme === Theme.WixStudio && isEligibleForRevShare) {
    const faqs = Object.entries(themedFaqs);
    const updatedFaqs = [
      ...faqs.slice(0, 5),
      ['revenue_share_payouts', 'studio_package_picker.FAQ.revshare'],
      ...faqs.slice(5),
    ];

    themedFaqs = Object.fromEntries(updatedFaqs);
  }

  if (localizedFaqs && !isEnterprisePlanType && theme !== Theme.WixStudio) {
    const specifiedCountryFaqsMap = getSpecifiedCountryFaqs({ country });
    if (specifiedCountryFaqsMap) {
      return Object.values(specifiedCountryFaqsMap).filter(Boolean);
    }
  }

  const faqMap =
    theme === Theme.WixStudio
      ? themedFaqs
      : isEnterprisePlanType
        ? faqEnterpriseCodeMap
        : {
          planCode: hasGoogleVoucher
            ? themedFaqs.premiumPlan
            : themedFaqs.premiumPlanNoVoucher,
          canITryCode: themedFaqs.canITry,
          domainCode: themedFaqs.myDomain,
          customDomainCode: themedFaqs.customDomain,
          emailAddressCode: themedFaqs.emailAddress,
          voucherCode: hasGoogleVoucher
            ? themedFaqs.googleVoucher
            : themedFaqs.billingInfo,
          whatOnlinepaymentsCode: themedFaqs.whatOnlinePayments,
          transition: themedFaqs?.transition,
          difference: themedFaqs?.difference,
          studio: themedFaqs?.studio,
        };
  return Object.values(faqMap).filter(Boolean);
};

export const getRecommendedSectionDataFromDealerOffers = (
  offers: ParsedDealerOffersMap,
  isSecondUpgrade: boolean,
) => {
  const { recommendedSection } = (
    isSecondUpgrade
      ? offers.get(RealEstateId.AppIntentElementsSecondUpgrade)
      : offers.get(RealEstateId.AppIntentElementsInitialPurchase)
  ) as ParsedAppIntentElements;
  const section =
    recommendedSection === SectionCategory.DEFAULT
      ? SectionCategory.COMPLETE_ECOMMERCE_PLATFORM
      : recommendedSection;
  const shouldShowBadge = recommendedSection !== SectionCategory.DEFAULT;

  return {
    recommendedSection: section,
    shouldShowBadgeByDealer: shouldShowBadge,
  };
};

export const getDiffBetweenDays = (start: Date, end: Date) => {
  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  const startUtc = Date.UTC(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
  );
  const endUtc = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.floor((endUtc - startUtc) / MS_PER_DAY);
};

export const creditExplanationTooltipData = (
  currentPlanDetails,
  availableCredit,
  todayDate = new Date(),
) => {
  const plansExpireTime = currentPlanDetails?.planEndDate;
  const daysLeft = getDiffBetweenDays(
    todayDate,
    new Date(plansExpireTime || 0),
  );
  const credit = availableCredit;
  const pricePerDay = daysLeft >= 1 ? (credit / daysLeft).toFixed(2) : 0; // Not supposed to get here.  If the plan is complete, and there are no days left, there is no 2nd upgrade.
  return {
    plan: currentPlanDetails?.planName,
    pricePerDay: +pricePerDay,
    daysLeft: +daysLeft,
    credit: +availableCredit,
  };
};

export const getCycleKey = (paymentCycle: PaymentCycle | undefined) => {
  switch (paymentCycle) {
    case PaymentCycle.MONTHLY:
      return 'Advanced_plans.second_upgrade.cycle.monthly';
    case PaymentCycle.YEARLY:
      return 'Advanced_plans.second_upgrade.cycle.yearly';
    case PaymentCycle.YEAR2:
      return 'Advanced_plans.second_upgrade.cycle.2year';
    case PaymentCycle.YEAR3:
      return 'Advanced_plans.second_upgrade.cycle.3year';
    default:
      return '';
  }
};

const getDomainTld = (domainName: string | undefined) => {
  const dotIndex = domainName?.indexOf('.');
  return dotIndex ? domainName?.substring(dotIndex + 1) : '';
};

export const isEligibleForVoucher = (
  domainName: string | undefined,
): boolean => {
  const tld = getDomainTld(domainName);
  return (
    !!tld &&
    Object.values(VoucherEligibleTlds).includes(tld as VoucherEligibleTlds)
  );
};

export const wixResultInterceptor = (response) => {
  // Handling deprecated convention in premium-store response
  if (!response.data) {
    return response;
  }

  if (response.data.success === false) {
    response.status = 500;
    return Promise.reject(response);
  }

  if (response.data.success === true && response.data.payload !== undefined) {
    const payload = response.data.payload;
    if (typeof payload === 'object' && !Array.isArray(payload)) {
      delete response.data.payload;
      delete response.data.success;
      delete response.data.errorCode;
      delete response.data.errorDescription;
      response.data = { ...response.data, ...payload };
    } else {
      response.data = response.data.payload;
    }
  }

  return response;
};

export const getBuildFeaturesFunc = ({
  experiments,
  userCountry,
  language,
  domainName,
  productIds,
  isUpgrade,
  referralInfo,
  vouchersTypeEligibility,
  advancedPlansProducts,
  isChangePlan,
}: {
  experiments: StringMap;
  userCountry: Country;
  domainName?: string;
  referralInfo?: string;
  productIds: string[];
  language;
  isUpgrade: boolean;
  vouchersTypeEligibility?: string[] | null;
  advancedPlansProducts: AdvancedPlansProductData[];
  isChangePlan: boolean;
}) => {
  const buildFeatures = (advacnedFeatures: AdvancedPlansFeature[]) =>
    new FeaturesBuilder(advacnedFeatures)
      .filterOnlyOfferingRelated(productIds)
      .filterIncludedInProducts(productIds)
      .filterByExpriments(experiments)
      .applyGeoFiltering(userCountry)
      .applyLangFiltering(language)
      .useDynamicAdVoucherMarketingFeature(experiments)
      .changeOneYearDomainFeature({
        userCountry,
        experiments,
        domainName,
        referralInfo,
      })
      .applySecondUpgradeFilter({
        experiments,
        isUpgrade,
        vouchersTypeEligibility,
        advancedPlansProducts,
        isChangePlan,
      })
      .build();
  return buildFeatures;
};

export const getCountryCodeTld = (country: Country) => {
  const ccTldMap: Record<string, string> = {
    [Country.DE]: '.de',
    [Country.BR]: '.com.br',
    [Country.FR]: '.fr',
    [Country.BE]: '.fr', // we want to show .fr also in belgium
  };
  return ccTldMap[country];
};

const padNumber = (n: number, padLength: number, padWith: string = '0') => {
  const pad = new Array(1 + padLength).join(padWith);
  const paddedString = (pad + n).slice(-pad.length);
  return paddedString;
};

export const getSaleEndTimeString = (saleEndDate: number, country: Country) => {
  const US_HOUR_AND_TIMEZONE = '(11:59 PM PT)';

  if (country === Country.USA || country === Country.CA) {
    return US_HOUR_AND_TIMEZONE;
  } else {
    const date = new Date(saleEndDate);
    const hrsString = padNumber(date.getUTCHours(), 2);
    const minString = padNumber(date.getUTCMinutes(), 2);
    return `(${hrsString}:${minString} GMT)`;
  }
};

export const formatSaleEndDate = ({
  saleEndDate,
  country,
  formatter,
}: {
  saleEndDate: number;
  country: Country;
  formatter: DateFormatter;
}) => {
  const hourAndTimezone = getSaleEndTimeString(saleEndDate, country);
  if (country === Country.USA || country === Country.CA) {
    const date = dayjs(saleEndDate).subtract(1, 'day').valueOf();
    return formatter(date).concat(' ', hourAndTimezone);
  }
  return formatter(saleEndDate).concat(' ', hourAndTimezone);
};

export const getProductFamilyIsInSale = (productFamily: ProductFamily) =>
  productFamily === ProductFamily.Pro ||
  productFamily === ProductFamily.Unlimited ||
  productFamily === ProductFamily.Evolve ||
  productFamily === ProductFamily.Grow ||
  productFamily === ProductFamily.VIP ||
  productFamily === ProductFamily.Ultra ||
  productFamily === ProductFamily.Extra ||
  productFamily === ProductFamily.Scale ||
  productFamily === ProductFamily.Boost ||
  productFamily === ProductFamily.Standard ||
  productFamily === ProductFamily.Advanced ||
  productFamily === ProductFamily.Core ||
  productFamily === ProductFamily.Business ||
  productFamily === ProductFamily.Elite ||
  productFamily === ProductFamily.Plus;

export const isSunriseExperimentsOn = (experiments: StringMap) =>
  (experiments[Experiments.SunriseV2] === 'true' &&
    experiments[Experiments.EnableSunriseV2] === 'true') ||
  experiments[Experiments.SunriseAnonymous] === 'true' ||
  experiments[Experiments.NewLowTierPlan] === 'true';

export const stringifyDefinedQueryParams = (params: {
  [key: string]: string;
}): string => {
  const definedParams = Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== '',
    ),
  );

  const queryParamsString = new URLSearchParams(definedParams).toString();
  return queryParamsString ? `?${queryParamsString}` : '';
};

export const onlyUnique = (value: any, index: number, array: any[]) => {
  return array.indexOf(value) === index;
};

export const buildEnterpriseUrlLink = ({
  localeUrl,
  referrer,
  locale,
  userGuid = 'null',
  clientId = 'null',
}: {
  localeUrl: string;
  referrer: string;
  locale: string;
  userGuid?: string;
  clientId?: string;
}): string => {
  const urlObj = new URL(localeUrl);
  urlObj.searchParams.append(
    'referrer',
    `${referrer}-${encodeURIComponent(locale)}`,
  );
  urlObj.searchParams.append('uuid', encodeURIComponent(userGuid));
  urlObj.searchParams.append('clientid', encodeURIComponent(clientId));

  return urlObj.toString();
};

export const shouldShowPremiumRequiredForDomain = (
  experiments: ExperimentsBag,
) => {
  const searchParams = new URLSearchParams(window.location.search);

  return (
    experiments[Experiments.PremiumRequiredForDomainMessage] === 'true' &&
    searchParams.get('referralAdditionalInfo') === 'add-domain-purchase-intent'
  );
};

export const shouldShowDomain = (experiments: ExperimentsBag) => {
  const searchParams = new URLSearchParams(window.location.search);

  return (
    experiments[Experiments.DisplayDomainNameInDomainFeature] === 'true' &&
    searchParams.get('showDomain') === 'true'
  );
};

export const getDomainName = (experiments: ExperimentsBag) => {
  const searchParams = new URLSearchParams(window.location.search);

  if (shouldShowDomain(experiments)) {
    return searchParams.get('domainName') ?? undefined;
  }

  return;
};

export const shouldShowDomainFirstItem = (experiments: ExperimentsBag) => {
  const searchParams = new URLSearchParams(window.location.search);

  return (
    experiments[Experiments.DomainFirstItemPurchaseIntent] === 'true' &&
    searchParams.get('referralAdditionalInfo') === 'add-domain-purchase-intent' &&
    searchParams.get('showDomain') === 'true'
  );
};

export const shouldShowConnectDomainTitle = (experiments: ExperimentsBag) => {
  const searchParams = new URLSearchParams(window.location.search);

  return (
    experiments[Experiments.ConnectDoaminPPTitle] === 'true' &&
    searchParams.get('referralAdditionalInfo') === 'add-domain-connect-intent'
  );
};

const getRevShareRateByProductAndCycle = (revShareData: RevShareData, productId: string, cycleId: PaymentCycle) => {
  const productRevShare = revShareData[productId];

  if (!productRevShare) {
    return undefined;
  }

  return cycleId === "MONTHLY" || cycleId === "QUARTERLY"
    ? productRevShare.monthlyRevShareRate
    : productRevShare.yearlyRevShareRate;
};

export const calculateCycleRevShare = ({ cycle, revShareData, productGuid }: calculateCycleRevShareProps) => {
  const { firstPeriodPriceMonthly, monthlyActualPrice, cycleId } = cycle || {};
  const cyclePrice = firstPeriodPriceMonthly || monthlyActualPrice;
  if (!cycleId || !cyclePrice || !productGuid) return 0;

  const revSharePercentage = getRevShareRateByProductAndCycle(revShareData, productGuid, cycleId);
  if (!revSharePercentage) {
    return 0;
  }
  return cyclePrice * CycleMultiplier[cycleId] * revSharePercentage / 100;
};

export const calculateRevShareSubtitleAmount = ({ currentStep, cycles, currentCycle, revShareData, productGuid }: calculateRevShareSubtitleAmountProps) => {
  if (!productGuid || !revShareData || !Array.isArray(cycles) || !cycles.length) {
    return 0;
  }

  if (currentStep === Step.Subscriptions) {
    return Math.max(...cycles.map(cycle =>
      calculateCycleRevShare({ cycle, revShareData, productGuid })
    ));
  }

  if (currentStep === Step.Checkout) {
    const currentCycleDetails = cycles.find(cycle => cycle.cycleId === currentCycle);
    if (!currentCycleDetails) return 0;
    return calculateCycleRevShare({ cycle: currentCycleDetails, revShareData, productGuid });
  }

  return 0;
};

export const getRevShareSubtitleKey = (currentStep: Step, revShareEarnedAmount: number) => {
  if (revShareEarnedAmount === 0) {
    return 'studio_package_picker.header.purchase.no_revshare.subtitle';
  }
  if (currentStep === Step.Subscriptions) {
    return 'studio_package_picker.header.billing_cycle.revshare.subtitle';
  }
  return 'studio_package_picker.header.purchase.revshare.subtitle';
};

export const getMaxSalePercentage = (products?: ProductInfo[]): number => {
  let maxPercentage = 0;

  if (!products) {
    return 0;
  }

  products.forEach(product => {
    if (product?.pricingInfo?.salePricing?.discountPrices) {
      const discountPrices = product.pricingInfo.salePricing.discountPrices;
      discountPrices.forEach(discount => {
        if (discount.percentage && discount.percentage > maxPercentage) {
          maxPercentage = discount.percentage;
        }
      });
    }
  });

  return maxPercentage;
};
